import { joiResolver } from '@hookform/resolvers/joi';
import { AxiosError } from 'axios';
import { useForm, UseFormReturn } from 'react-hook-form';
import { HtmlTrans, stringToPayloadFormat, useSnackbarHandler } from 'web-library';
import { RetrieveAuditSettingsResponse } from '../../../api-refactored/audit-settings/useGetRetrieveAuditSettings';
import {
  AuditSettingsRequest,
  AuditSettingsResponse,
  usePutAuditSettings,
} from '../../../api-refactored/audit-settings/usePutAuditSettings';
import { UseCustomMutationReturn } from '../../../api-refactored/utils/useCustomMutation';
import { AuditSettingForm } from '../../../models';
import { repeatData, reviewReminderData } from '../components/AdminAuditSettings';
import auditSettingsSchema from '../schema/auditSettingsSchema';

interface AuditSettingsReturn {
  /**
   * Perform submit action, either to call block call or unblock call, depends on the account status
   */

  /**
   * Form to send edit & delete
   */

  formHook: UseFormReturn<AuditSettingForm>;
  /**
   * React query mutation object
   */
  handlePutAuditSettings: (formData: AuditSettingForm) => void;
  auditSettingsMutation: UseCustomMutationReturn<
    AuditSettingsResponse,
    AxiosError<any>,
    AuditSettingsRequest,
    unknown
  >;
}

/**
 * A hook to handle search functionality
 * @returns
 */

interface UseAuditSettingProps {
  auditSettingsData: RetrieveAuditSettingsResponse | undefined;
  requestType: string;
  requestName?: string;
}

export const useAuditSettings = ({
  auditSettingsData,
  requestType,
  requestName,
}: UseAuditSettingProps): AuditSettingsReturn => {
  const { snackbarHandler } = useSnackbarHandler();

  const auditSettingsMutation = usePutAuditSettings({
    reportType: requestType,
    onSuccess: () => {
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: (
          <HtmlTrans
            i18nKey="admin->>AuditSettings.SuccessMsg"
            values={{ reportType: requestName }}
          />
        ),
      });
    },
  });

  const repeatDefault =
    repeatData.find((v) => v.code === auditSettingsData?.repeat) || repeatData[0];
  const reminderReviewRepeatDefault =
    reviewReminderData.find((v) => v.code === auditSettingsData?.reminderReviewRepeat) ||
    reviewReminderData[0];

  const formHook = useForm<AuditSettingForm>({
    mode: 'onSubmit',
    resolver: joiResolver(auditSettingsSchema),
    defaultValues: {
      firstGeneratedDate: auditSettingsData?.firstGeneratedDate,
      reviewDeadline: auditSettingsData?.reviewDeadLine,
      reminderReviewRepeat: reminderReviewRepeatDefault,
      repeat: repeatDefault,
    },
  });

  const handlePutAuditSettings = (formData: AuditSettingForm) => {
    auditSettingsMutation.mutate({
      firstGeneratedDate: stringToPayloadFormat(formData.firstGeneratedDate as string), // 2022-10-03
      reminderReviewRepeat: formData.reminderReviewRepeat.code,
      repeat: formData.repeat.code,
      reviewDeadLine: formData.reviewDeadline,
      uuid: auditSettingsData?.uuid || '',
    });
  };

  return {
    formHook,
    handlePutAuditSettings,
    auditSettingsMutation,
  };
};
